import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useRef,
  useState
} from 'react';
import * as Sentry from '@sentry/browser';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { updateOidcUser } from 'src/api/capacitor-adapter';
import { SplashScreen } from 'src/components/splash-screen';
import { useNotificationsContext } from 'src/hooks/use-context';
import { useHookMemo } from 'src/hooks/use-hook-memo';
import { useMountEffect } from 'src/hooks/use-mount-effect';
import { messageServiceWorker } from 'src/libs/sw-registration';
import { AuthContextType } from './auth-context';

export interface OidcCallbacks {
  // signInWithEmailLink: (email: string, url: string) => Promise<void>;
  signInRedirect: () => Promise<void>;
  signInCallback: () => Promise<void>;
  signOut: (reason?: string) => Promise<void>;
  updatePassword: undefined; // (current: string, password: string) => Promise<void>;
  // resetPassword: (email: string) => Promise<void>;
  // resetPasswordWithEmailLink: (
  //   oobCode: string,
  //   password: string
  // ) => Promise<void>;
  // validateOobCode: (oobCode: string) => Promise<string>;
  // development tools
  // saveSession: (storageId: string, sessionName: string) => void;
  // loadSession: (storage: string) => void;
}

/**
 * Provides user authentication state
 */
export interface OidcAuthState {
  // login state
  initialized: boolean;
  authenticated: boolean;
  failed: boolean;
  // OIDC user (provided for re-renders)
  oidcUser?: User;
}

const initialState: OidcAuthState = {
  initialized: false,
  authenticated: false,
  failed: false
};

export type OidcContextType = { type: 'oidc' } & AuthContextType &
  OidcCallbacks &
  OidcAuthState;

export const OidcContext = createContext<OidcContextType | undefined>(
  undefined
);

const handleOidcAuthChanged = (oidcUser?: User | null) => {
  console.info('[oidc] Authentication changed:', !!oidcUser);

  if (!oidcUser || oidcUser.expired)
    return {
      initialized: true,
      authenticated: false,
      failed: false
    };

  return {
    initialized: true,
    authenticated: true,
    failed: false,
    oidcUser
  };
};

/**
 * Helper to improve authentication debugging
 */
const withDebugContext = async <T extends any>(
  debugMessage: string,
  debugExtra: Record<string, unknown>,
  callback: () => Promise<T> | undefined
): Promise<void> => {
  console.info(`[oidc] ${debugMessage}`);
  await callback()?.catch((error) => {
    // ensure the error is properly captured with Sentry
    // (this is critical for login error debugging)
    Sentry.captureException(error, {
      extra: {
        ...debugExtra,
        currentTenantId: 'TODO!'
      }
    });
    // re-throw the error to the caller
    throw error;
  });
};

/**
 * Provide the OIDC logic to children
 *
 * NOTE: This component blocks rendering until the OIDC context has been
 * initialized properly. Children can rely on the context being ready.
 */
export const OidcProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;

  const userManager = useRef<UserManager>();
  const [state, setState] = useState<OidcAuthState>(initialState);
  const { handleRequestPermissions } = useNotificationsContext();

  const handleState = useCallback(
    async (state: OidcAuthState) => {
      setState(state);
      // provide the token to the service worker
      await messageServiceWorker('OIDC_TOKEN', state.oidcUser?.access_token);
    },
    // must remain stable to avoid re-rendering the context
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // initialize OIDC
  useMountEffect(() => {
    console.info('[oidc] Initialization');
    if (!globalThis.__OIDC__) throw new Error('[oidc] Missing configuration');

    userManager.current = new UserManager({
      // tenant specific configurtion
      client_id: globalThis.__OIDC__.clientId,
      authority: globalThis.__OIDC__.authority,
      redirect_uri: globalThis.__OIDC__.redirectUri,
      post_logout_redirect_uri: globalThis.__OIDC__.postLogoutRedirectUri,
      // PKCE + auth code
      response_type: 'code',
      scope: 'openid email profile',
      // internal configuration
      automaticSilentRenew: false,
      loadUserInfo: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      accessTokenExpiringNotificationTimeInSeconds: 600,
      extraQueryParams: {
        // do not silently skip the login page
        // (for better logout behavior)
        prompt: 'select_account'
      }
    });

    const callback = () => {
      userManager.current?.getUser().then(async (user) => {
        console.info(
          user
            ? `[oidc] Token expires in ${user.expires_in}s`
            : '[oidc] Not authenticated'
        );

        // inject the user into the API (no re-render)
        updateOidcUser(user);
        // update the local state (and cause re-render after API is ready)
        handleState(handleOidcAuthChanged(user));
        // request notifications when authentication changes
        if (!!user) await handleRequestPermissions('OIDC auth changed');
      });
    };

    userManager.current.events.addUserLoaded(callback);
    userManager.current.events.addUserUnloaded(callback);

    // ensure refreshed token is forwarded
    userManager.current.events.addAccessTokenExpiring(() =>
      withDebugContext('Attempting token refresh', {}, () =>
        userManager.current?.signinSilent()
      )
    );

    // load the initial state (and raise event)
    userManager.current.getUser().then(callback);
  });

  const signInRedirect = useCallback(
    (): Promise<void> =>
      withDebugContext('Sign in with redirect', {}, () =>
        userManager.current?.signinRedirect()
      ),
    []
  );

  const signInCallback = useCallback(
    (): Promise<void> =>
      withDebugContext('Sign in callback', {}, () =>
        userManager.current?.signinCallback()
      ),
    []
  );

  const signOut = useCallback(
    (): Promise<void> =>
      withDebugContext('Sign out', {}, () =>
        // currently no logout endpoints expected
        userManager.current?.removeUser()
      ),
    []
  );

  const context = useHookMemo({
    type: 'oidc' as const,
    // auth state
    initialized: state.initialized,
    authenticated: state.authenticated,
    failed: state.failed,
    // OIDC callbacks
    signInRedirect,
    signInCallback,
    signOut,
    // not supported in this context
    updatePassword: undefined
  });

  // children rely on valid context
  if (!state.initialized || state.failed) return <SplashScreen />;

  return (
    <OidcContext.Provider value={context}>{children}</OidcContext.Provider>
  );
};
